// The total we show is dependent on which supercategories are selected
// Principles:
// - Transfers are only counted if they are the only category being viewed
// - Saving & Investing only counted if it is the only category being viewed, or if it is the only category being viewed with Transfers
// - Otherwise, Spending and Income are counted
import { useMemo } from 'react';
import { app } from 'zapatos/schema';
import { TransactionEntryCategoryFieldsFragment } from '../../graphql/generated';
import { sum } from '../../util/arrays';
import { typedKeys } from '../../util/objects';
import { getTransactionsForSupercategories } from './getTransactionsForSuperCategories';
import { CashflowTransactionEntry } from './types';
import useSelectedSupercategories from './useSelectedSupercategories';

export const useTotal = (
    transactionEntries: CashflowTransactionEntry[],
    categoryIds: string[] | null,
    transactionEntryCategoriesMapById: Record<
        string,
        TransactionEntryCategoryFieldsFragment
    >
) => {
    const selectedSupercategories = useSelectedSupercategories({
        categoryIds,
        transactionEntryCategoriesMapById,
    });

    return useMemo(() => {
        let supercategories: app.supercategory[];

        if (
            selectedSupercategories.includes('SPENDING') ||
            selectedSupercategories.includes('INCOME')
        ) {
            supercategories = ['SPENDING', 'INCOME'];
        } else if (
            selectedSupercategories.length === 1 &&
            (selectedSupercategories[0] === 'SAVING_INVESTING' ||
                selectedSupercategories[0] === 'TRANSFERS')
        ) {
            supercategories = selectedSupercategories;
        } else {
            supercategories = ['SAVING_INVESTING'];
        }

        const filteredTransactionEntries = getTransactionsForSupercategories(
            transactionEntries,
            supercategories
        );

        return sum(filteredTransactionEntries, (t) => t.amount);
    }, [transactionEntries, selectedSupercategories]);
};

export const useTotals = (transactionEntries: CashflowTransactionEntry[]) =>
    useMemo(() => {
        const totals: Record<app.supercategory, number> = {
            SPENDING: 0,
            INCOME: 0,
            SAVING_INVESTING: 0,
            TRANSFERS: 0,
        };

        typedKeys(totals).forEach((key) => {
            totals[key] = sum(
                getTransactionsForSupercategories(transactionEntries, [key]),
                (t) => t.amount
            );
        });

        return totals;
    }, [transactionEntries]);
